import { uniqBy, uniq } from 'lodash';
import moment from 'moment';

import {
  INVOICE_GET_PURCHASE_LOCATIONS,
  INVOICE_SET_CLINIC,
  INVOICE_SET_CLINICS,
  INVOICE_SET_DATE,
  INVOICE_SET_PURCHASE_LOCATION,
  INVOICE_PRODUCTS_LOADING,
  INVOICE_GET_PRODUCTS,
  INVOICE_UPDATE_LINE_ITEMS,
  INVOICE_CLEAR,
  INVOICE_VERIFICATION_SUCCESS,
  INVOICE_UPDATE_PROMO_CODES,
  INVOICE_ERROR,
  INVOICE_IS_SUBMITTING,
  INVOICE_SET_RECEIPT_ERROR,
  INVOICE_SAVE_USER,
  INVOICE_SET_SUCCESS_MESSAGE,
  INVOICE_SET_TRANSACTION_ID,
  INVOICE_SET_RECEIPT_TO_BE_REPLACED,
  INVOICE_SET_RECEIPTS,
  INVOICE_SET_RECEIPTS_LOADING,
  INVOICE_REMOVE_RECEIPT,
} from '../constants/invoice';

/**
 * Resets invoice store back to initial state
 */
export const invoiceClear = (clearUser = true) => (dispatch) => {
  dispatch({
    type: INVOICE_CLEAR,
    clearUser,
  });
};

/**
 * Gets list of purchase locations required for purchase location select
 * @param {object} param.$http - the angular $http lib
 * @param {object} param.ENV - the ENV service
 * @returns {Promise}
 */
export const invoiceGetPurchaseLocations = ({ $http, ENV }) => (dispatch, getStore) =>
  new Promise((resolve, reject) => {
    // use cached value for purchase locations on store if available
    const { availablePurchaseLocations } = getStore().invoice;
    if (availablePurchaseLocations && availablePurchaseLocations.length > 0) {
      return Promise.resolve(availablePurchaseLocations);
    }

    $http({
      method: 'GET',
      url: ENV.apiEndpoint + `/1.0/transactions/purchase_locations/`,
    })
      .then((response) => {
        const { purchaseLocations } = response.data;
        dispatch({
          type: INVOICE_GET_PURCHASE_LOCATIONS,
          availablePurchaseLocations: purchaseLocations,
        });
        resolve(purchaseLocations);
      })
      .catch((error) => {
        dispatch({
          type: INVOICE_ERROR,
          message: 'We encountered a problem fetching purchase locations.',
          error,
        });
        reject(error);
      });
  });

/**
 * Sets the purchase location associated with the invoice
 * @param {object} purchaseLocation - where the user puchased the product
 */
export const invoiceSetPurchaseLocation = (purchaseLocation) => ({
  type: INVOICE_SET_PURCHASE_LOCATION,
  purchaseLocation,
});

/**
 * Gets list of available products for invoice upload
 * @param {string} param.orgId - the atlas organization, some products are only available for specific orgs
 * @param {object} param.$http - angular lib
 * @param {object} param.ENV - angular ENV service
 * @returns {Promise}
 */
export const invoiceGetProducts = ({ $http, ENV }) => (dispatch, getStore) =>
  new Promise((resolve, reject) => {
    const { clinic, datePurchased } = getStore().invoice;

    dispatch({
      type: INVOICE_PRODUCTS_LOADING,
    });

    const params = {
      organizations: clinic?.id,
      date_purchased:
        datePurchased && moment(datePurchased).toISOString().split('T')[0]?.replace(/-/g, ''),
    };

    const queryString = Object.keys(params)
      .map((key) => params[key] && key + '=' + params[key])
      .filter((x) => x)
      .join('&');

    $http({
      method: 'GET',
      url: `${ENV.apiEndpoint}/1.0/products/?${queryString}`,
    })
      .then((response) => {
        const products = response.data.products?.map((product) => ({
          ...product,
          // we add the associated species to each product to make it easier to filter later
          species: product?.skus?.reduce((species, sku) => {
            return uniq(species.concat(sku?.species));
          }, []),
        }));

        dispatch({
          type: INVOICE_GET_PRODUCTS,
          products,
        });

        resolve(products);
        // TODO we have to check any items in store against available products. In case some one changes org after setting items.
      })
      .catch((error) => {
        dispatch({
          type: INVOICE_ERROR,
          message: 'We encountered a problem fetching products.',
          error,
        });
        reject(error);
      });
  });

/**
 * Sets the date for the invoice
 * @param {string} date - date string in MM/DD/YYYY
 * @param {boolean} isValid - is the date valid
 * @returns
 */
export const invoiceSetDate = (date, isValid) => ({
  type: INVOICE_SET_DATE,
  date,
  isValid,
});

/**
 * Sets the clinic on the invoice store.
 * @param {object} param.clinic - the Atlas organization
 * @param {object} param.$http - the angular $http lib
 * @param {object} param.ENV - the angular ENV service
 */
export const invoiceSetClinic = ({ clinic }) => ({
  type: INVOICE_SET_CLINIC,
  clinic,
});

/**
 * Update the list of available clinics when a user selects a clinic from the vet finder
 * @param {object} clinic
 */
export const invoiceAddClinicToAvailableClinics = (clinic) => (dispatch, getStore) => {
  if (!clinic) return;

  const updatedClinics = uniqBy(
    [clinic].concat(getStore().invoice.availableClinics),
    (org) => org.id,
  );
  dispatch({
    type: INVOICE_SET_CLINICS,
    availableClinics: updatedClinics,
  });
};

/**
 * Gets the clinics associated with the user's pets
 * @param {object} param.$http - the angular $http lib
 * @param {object} param.ENV - the angular ENV service
 * @returns {Promise}
 */
export const invoiceGetClinicsForPets = ({ $http, ENV }) => (dispatch, getStore) =>
  new Promise((resolve, reject) => {
    const {
      pet: { userPets = [] },
    } = getStore();
    const petIds = userPets.map((pet) => pet.is_active && pet.id).filter((pet) => pet);

    Promise.all(
      petIds.map((id) =>
        $http({
          method: 'GET',
          url: ENV.apiEndpoint + `/1.0/pets/${id}/providers/primary/`,
          params: { petId: id },
        }),
      ),
    )
      .then((responses) => {
        const orgs = responses
          .map(
            (response) =>
              !response?.data?.petProvider?.organization.is_default_org &&
              response?.data?.petProvider?.organization,
          )
          .filter((org) => org);
        const uniqueOrgs = uniqBy(orgs, (org) => org.id);
        dispatch({
          type: INVOICE_SET_CLINICS,
          availableClinics: uniqueOrgs,
        });
        resolve(uniqueOrgs);
      })
      .catch((error) => {
        dispatch({
          type: INVOICE_ERROR,
          message: 'We encountered a problem fetching clinics associated with this user.',
          error,
        });
        reject(error);
      });
  });

/**
 * Sets the clinics associated with the user's pets
 * @param {object} param.organizations - list of organizations for user
 * @returns {Promise}
 */
export const invoiceSetClinicsForPets = (organizations) => (dispatch) =>
  new Promise((resolve, reject) => {
    if (!organizations) {
      dispatch({
        type: INVOICE_ERROR,
        message: 'We encountered a problem fetching clinics associated with this user.',
      });
      reject();
    }
    const orgs = organizations.filter((org) => org);
    const uniqueOrgs = uniqBy(orgs, (org) => org.id);
    dispatch({
      type: INVOICE_SET_CLINICS,
      availableClinics: uniqueOrgs,
    });
    resolve(uniqueOrgs);
  });

/**
 * Uploads receipt files to atlas ( not transactions details )
 * @param {string} param.file - the blob we are uploading
 * @param {string} param.userId - the atlas user id
 * @param {object} param.Upload - angular Upload lib
 * @param {string} param.accessToken - user's access token
 * @param {object} param.ENV - angular ENV service
 * @returns {Promise}
 */
export const invoiceUploadReceipts = ({ files, userId, Upload, accessToken, ENV }) => async (
  dispatch,
  getStore,
) => {
  const { isFileUploading, receiptToBeReplaced } = getStore().invoice;
  let isReplacingReceipt = !!receiptToBeReplaced;
  let transaction_id = getStore().invoice?.transaction_id;
  if (isFileUploading) {
    return;
  }
  if (!files) {
    throw new Error('Missing invoice');
  }

  dispatch({
    type: INVOICE_SET_RECEIPTS_LOADING,
  });

  // first receipt gets posted to receipts endpoint and is used to create the transaction
  const isFirstReceipt = !transaction_id;
  const uploadedReceipts = [];

  if (isFirstReceipt) {
    try {
      const { data: { receipts } = {} } = await Upload.upload({
        method: 'POST',
        url: ENV.apiEndpoint + '/1.0/receipts/',
        data: {
          image: files[0],
          user_id: userId,
        },
        headers: { Authorization: 'Bearer ' + accessToken },
        disableProgress: false,
      });

      transaction_id = receipts.transaction_id;
      uploadedReceipts.push({
        name: files[0]?.name,
        type: files[0]?.type,
        size: files[0]?.size,
        id: receipts.receipt_id,
        ...receipts,
      });
      dispatch({
        type: INVOICE_SET_TRANSACTION_ID,
        transaction_id,
      });
    } catch (error) {
      dispatch({
        type: INVOICE_SET_RECEIPT_ERROR,
        message: 'We encountered a problem uploading this receipt.',
        error,
      });
    }
  }

  // remaining receipts get uploaded one at a time to receipts endpoint
  const remainingFiles = isFirstReceipt ? files.slice(1) : files;
  for (let i = 0; i < remainingFiles.length; i++) {
    const file = remainingFiles[i];
    try {
      const url = `${ENV.apiEndpoint}/1.0/transactions/${transaction_id}/receipts/${
        isReplacingReceipt ? `${receiptToBeReplaced.id}/` : ''
      }`;
      const method = isReplacingReceipt ? 'PUT' : 'POST';
      // eslint-disable-next-line no-await-in-loop
      const { data: { transaction: { receipts } = {} } = {} } = await Upload.upload({
        method,
        url,
        data: {
          image: file,
          user_id: userId,
        },
        headers: { Authorization: 'Bearer ' + accessToken },
        disableProgress: false,
      });

      uploadedReceipts.push({
        name: file?.name,
        type: file?.type,
        size: file?.size,
        ...receipts[receipts?.length - 1],
      });

      if (isReplacingReceipt) {
        isReplacingReceipt = false;
        dispatch({
          type: INVOICE_SET_RECEIPT_TO_BE_REPLACED,
          receipt: null,
        });
      }
    } catch (error) {
      dispatch({
        type: INVOICE_SET_RECEIPT_ERROR,
        message: 'We encountered a problem uploading this receipt.',
        error,
      });
    }
  }

  dispatch({
    type: INVOICE_SET_RECEIPTS,
    receipts: uploadedReceipts,
  });
};

export const invoiceRemoveReceipt = ({ $http, ENV, receipt }) => async (dispatch, getStore) => {
  /* 
    If there is only one receipt and the user wants to delete it, we can't actually delete it because atlas requires at least one receipt on the transaction.
    Instead, we have to save a reference to the receipt in the store and remove it from the receipts array.
    When the user uploads a new receipt and there is a reference to the old receipt, 
    we have to make a put request to the receipts endpoint to update the receipt the user wanted to delete. 
  */
  const { receipts, transaction_id } = getStore().invoice;
  if (receipts.length === 1) {
    return dispatch({
      type: INVOICE_SET_RECEIPT_TO_BE_REPLACED,
      receipt,
    });
  }

  try {
    await $http({
      method: 'DELETE',
      url: `${ENV.apiEndpoint}/1.0/transactions/${transaction_id}/receipts/${receipt?.id}/`,
    });
    dispatch({
      type: INVOICE_REMOVE_RECEIPT,
      receipt,
    });
  } catch (error) {
    dispatch({
      type: INVOICE_SET_RECEIPT_ERROR,
      message: 'We encountered a problem removing this receipt.',
      error,
    });
  }
};

/**
 * Set state of showSuccessMessage (true|false)
 */
export const setInvoiceSuccessMessage = (showSuccessMessage) => (dispatch) => {
  dispatch({
    type: INVOICE_SET_SUCCESS_MESSAGE,
    showSuccessMessage: !!showSuccessMessage,
  });
};

/**
 * Submits the transaction details to Atlas.
 * If verify param is true, the transaction is only verified for points and promocodes
 * @param {object} param.$http - the angular $http lib
 * @param {object} param.ENV - the angular ENV service
 * @param {boolean} param.verify - if true the transaction is only submitted for verification of points and promo codes.
 * @returns {Promise}
 */
export const invoiceSubmitTransaction = ({ $http, ENV, verify }) => (dispatch, getStore) =>
  new Promise((resolve, reject) => {
    const { items, transaction_id, clinic, datePurchased, purchaseLocation, promoCodes, receipts } =
      getStore().invoice || {};

    dispatch({
      type: INVOICE_IS_SUBMITTING,
      verify,
    });

    const user_id = getStore().user.user.id;
    const invoicePayloadItems = items
      .map((item) => {
        const { pet = {}, product: { brand } = {}, price, quantity, sku } = item;
        if (!sku || !pet) {
          return;
        }
        // ensure dose unit and quantity are both numbers before multiplying
        const doseNumber = !Number.isNaN(parseInt(sku?.doses)) ? parseInt(sku?.doses) : 1;
        const quantityNumber = !Number.isNaN(parseInt(quantity)) ? parseInt(quantity) : 1;
        const doses = doseNumber * quantityNumber;
        return {
          pet_id: pet.id,
          sku: sku.sku,
          quantity,
          brand,
          doses,
          item_price: typeof price === 'string' ? parseFloat(price.replace('$', '')) : price,
        };
      })
      .filter((item) => item);

    const transaction = {
      items: invoicePayloadItems,
      user_id,
      receipts: receipts.map(({ id }) => ({ id })),
      transaction_id,
      organization_id: clinic.id,
      purchase_location_id: purchaseLocation?.id,
      date_purchased: moment(datePurchased).toISOString(),
      promotion_codes: promoCodes,
      verify,
    };

    const timeoutDuration = 1000 * 30; // timeout after 30 seconds

    const startingTime = new Date();

    $http({
      method: 'PUT',
      url: ENV.apiEndpoint + `/1.0/transactions/${getStore().invoice.transaction_id}/`,
      data: {
        transaction,
      },
      timeout: timeoutDuration, // timeout after a minute
    })
      .then((response) => {
        // if we are verifying set verification object on store.
        // otherwise clear invoice store
        if (verify) {
          dispatch({
            type: INVOICE_VERIFICATION_SUCCESS,
            verification: {
              transactionPointValue: response.data?.transaction?.points_awaiting_fulfillment, // items + promotion point value
              promotionPointValue:
                response.data?.transaction?.promotion_points_awaiting_fulfillment, // just promotion point value
              availablePromotionCodes: response.data?.transaction?.promotion_codes,
            },
          });
        } else {
          dispatch(setInvoiceSuccessMessage(true));
          setTimeout(() => {
            dispatch(setInvoiceSuccessMessage(false));
          }, 5000);
          dispatch(invoiceClear());
        }
        resolve(response.data);
      })
      .catch((error) => {
        const elasped = new Date() - startingTime;

        if (elasped >= timeoutDuration) {
          dispatch({
            type: INVOICE_ERROR,
            message: 'The request has timed out, please try again later',
            error,
          });
        } else {
          let errorMessage = error?.data?.transaction?.error;
          const is_invalid_products_error = error?.data?.transaction?.error?.includes(
            'products added is not valid',
          );

          if (errorMessage?.indexOf('Duplicate receipt_image_id') >= 0) {
            dispatch({
              type: INVOICE_ERROR,
              message: 'This invoice image has already been used. Please choose a different file.',
              error,
            });
          } else if (is_invalid_products_error) {
            dispatch({
              type: INVOICE_ERROR,
              message: 'One of the products added is not valid for the purchase date selected.',
              error,
            });
          } else if (verify) {
            dispatch({
              type: INVOICE_ERROR,
              message:
                errorMessage || 'We encountered a problem verifying points for this transaction.',
              error,
            });
          } else {
            dispatch({
              type: INVOICE_ERROR,
              message: errorMessage || 'We encountered a problem submitting this transaction.',
              error,
            });
          }
        }

        reject(error);
      });
  });

/**
 * Adds a line item to the invoice and verifies the points for the transaction
 * @param {object} param.item - the line item on the receipt
 * @param {object} param.$http - the angular $http lib
 * @param {object} param.ENV - the angular ENV service
 */
export const invoiceAddLineItem = ({ item, $http, ENV }) => (dispatch, getStore) => {
  const { items = [] } = getStore().invoice;
  const updatedItems = items.concat([
    {
      ...item,
      itemIndex: items.length,
    },
  ]);

  dispatch({
    type: INVOICE_UPDATE_LINE_ITEMS,
    items: updatedItems,
  });

  dispatch(invoiceSubmitTransaction({ $http, ENV, verify: true }));
};

/**
 * Updates a line item on the invoice and verifies the points for the transaction
 * @param {object} param.item - the line item on the receipt
 * @param {object} param.$http - the angular $http lib
 * @param {object} param.ENV - the angular ENV service
 */
export const invoiceUpdateLineItem = ({ item, $http, ENV }) => (dispatch, getStore) => {
  const {
    items = [],
    verification: { availablePromotionCodes },
    promoCodes,
  } = getStore().invoice;

  // exit if we don't have an item or we don't have an index
  if (!item || Number.isNaN(Number.parseInt(item.itemIndex)) || item.itemIndex < 0) return;
  const updatedItems = items.concat([]);
  updatedItems.splice(item.itemIndex, 1, item);

  dispatch({
    type: INVOICE_UPDATE_LINE_ITEMS,
    items: updatedItems,
  });

  // when an item is edited we also have to check that all the selected promo codes are still valid
  if (promoCodes && promoCodes.length > 0) {
    const updatedPromoCodes = promoCodes.filter((code) => {
      const activity = availablePromotionCodes.find((x) => x.code === code);
      const matchingItem = activity.items.find((x) => {
        return updatedItems.find((y) => x.product.sku === y.sku.sku);
      });
      return matchingItem;
    });

    dispatch({
      type: INVOICE_UPDATE_PROMO_CODES,
      promoCodes: updatedPromoCodes,
    });
  }

  dispatch(invoiceSubmitTransaction({ $http, ENV, verify: true }));
};

/**
 * Removes a line item on the invoice and verifies the points for the transaction
 * @param {object} param.item - the line item on the receipt
 * @param {object} param.$http - the angular $http lib
 * @param {object} param.ENV - the angular ENV service
 */
export const invoiceDeleteLineItem = ({ item, $http, ENV }) => (dispatch, getStore) => {
  const {
    items = [],
    verification: { availablePromotionCodes },
    promoCodes,
  } = getStore().invoice;

  const updatedItems = items
    .filter((x) => x.itemIndex !== item.itemIndex)
    .map((x, index) => ({
      ...x,
      itemIndex: index,
    }));

  dispatch({
    type: INVOICE_UPDATE_LINE_ITEMS,
    items: updatedItems,
  });

  // when an item is removed we also have to remove any associated promo codes
  if (promoCodes && promoCodes.length > 0) {
    const updatedPromoCodes = promoCodes.filter((code) => {
      const activity = availablePromotionCodes.find((x) => x.code === code);
      const matchingItem = activity.items.find((x) => {
        return updatedItems.find((y) => x.product.sku === y.sku.sku);
      });
      return matchingItem;
    });

    dispatch({
      type: INVOICE_UPDATE_PROMO_CODES,
      promoCodes: updatedPromoCodes,
    });
  }

  dispatch(invoiceSubmitTransaction({ $http, ENV, verify: true }));
};

/**
 * Adds or removes a promotional code to the transaction.
 * And verifies the points for the transaction.
 * @param {string} param.code - the promotional code
 * @param {object} param.$http - the angular $http lib
 * @param {object} param.ENV - the angular ENV service
 */
export const invoiceTogglePromoCode = ({ code, $http, ENV }) => (dispatch, getStore) => {
  const { promoCodes = [] } = getStore().invoice;
  const updatedPromoCodes = [].concat(promoCodes);
  const index = updatedPromoCodes.indexOf(code);
  // if code is in list remove it
  if (index >= 0) {
    updatedPromoCodes.splice(index, 1);
  }
  // else add it
  else {
    updatedPromoCodes.push(code);
  }
  dispatch({
    type: INVOICE_UPDATE_PROMO_CODES,
    promoCodes: updatedPromoCodes,
  });

  dispatch(invoiceSubmitTransaction({ $http, ENV, verify: true }));
};

/**
 * Saves user information to state
 * @param {string} param.user - the user to store in state.invoice.user
 */
export const saveInvoiceUser = (user) => (dispatch) => {
  dispatch({
    type: INVOICE_SAVE_USER,
    user: user,
  });
};
